import Papa from "papaparse";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { ButtonGroup } from "~/components/ui/button-group";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";

import { LineChartSection } from "./LineChartSection";
import { processSingleTestAccelerometerData } from "./accelerometerData.utils";
import { createDefaultOrientationSample } from "./accelerometerOrientation.utils";
import { extractActuatorIntervals } from "./dataProcessing.utils";
import {
  ResistanceViewMode,
  transformLinesForCapacitanceView,
  transformLinesForResistanceView,
} from "./derivative.utils";
import { FormattedChartData, SensorData } from "./types";
import { LazyChartSection } from "./LazyChartSection";
import { OrientationCube } from "./OrientationCube";

interface SingleSensorDataChartsProps {
  tests: {
    sampleFileUrl: string;
    testName?: string;
  }[];
}

export const SingleSensorDataCharts: React.FC<SingleSensorDataChartsProps> = ({
  tests,
}) => {
  const [testsData, setTestsData] = useState<(SensorData | null)[]>([]);
  const [hasE12Error, setHasE12Error] = useState<boolean[]>([]);
  const [hasHighRH, setHasHighRH] = useState<boolean[]>([]);
  const [resistanceView, setResistanceView] =
    useState<ResistanceViewMode>("raw");
  const [isResistanceSmoothed, setIsResistanceSmoothed] = useState(false);
  const [capacitanceView, setCapacitanceView] =
    useState<ResistanceViewMode>("raw");
  const [isCapacitanceSmoothed, setIsCapacitanceSmoothed] = useState(false);
  const [hoveredAccelerometerIndex, setHoveredAccelerometerIndex] = useState<
    number | null
  >(null);

  useEffect(() => {
    const processAllTests = async () => {
      const processedTests = await Promise.all(
        tests.map(async (test, index) => {
          try {
            const csvUrl = test.sampleFileUrl.replace(".json", ".csv");
            const response = await fetch(csvUrl);
            const csvText = await response.text();

            const parsed = Papa.parse(csvText, { header: true });
            const rows = parsed.data as Record<string, string>[];

            const processedData: SensorData = {
              resistance: [],
              capacitance: [],
              trh: [],
              accelerometer: [],
              actuators: extractActuatorIntervals(rows),
            };

            rows.forEach((row) => {
              const timestamp = row["timestamp"] || "";

              const resistanceValues = Array.from({ length: 6 }, (_, i) =>
                Number(row[`rSensor.Sensor${i + 1}`])
              );
              processedData.resistance.push({
                timestamp,
                values: resistanceValues,
              });

              const capacitanceValues = Array.from({ length: 6 }, (_, i) =>
                Number(row[`cSensor.Sensor${i + 1}`])
              );
              processedData.capacitance.push({
                timestamp,
                values: capacitanceValues,
              });

              processedData.trh.push({
                timestamp,
                cassetteTemp: Number(row["cassette.t"]),
                cassetteRH: Number(row["cassette.rh"]),
                internalTemp: Number(row["internal.t"]),
                internalRH: Number(row["internal.rh"]),
                externalTemp: Number(row["external.t"]),
                externalRH: Number(row["external.rh"]),
              });

              const x = Number(row["accelerometer.x"]);
              const y = Number(row["accelerometer.y"]);
              const z = Number(row["accelerometer.z"]);

              if (!Number.isNaN(x) || !Number.isNaN(y) || !Number.isNaN(z)) {
                processedData.accelerometer.push({
                  timestamp,
                  x: Number.isNaN(x) ? 0 : x,
                  y: Number.isNaN(y) ? 0 : y,
                  z: Number.isNaN(z) ? 0 : z,
                });
              }
            });

            const hasHighResistance = processedData.resistance.some((reading) =>
              reading.values.some(
                (value) => value > 2e12 && Number.isFinite(value)
              )
            );

            const first10RH = processedData.trh.slice(0, 10);
            const hasHighHumidity = first10RH.some(
              (reading) => reading.cassetteRH > 93
            );

            return {
              data: processedData,
              hasE12Error: hasHighResistance,
              hasHighRH: hasHighHumidity,
            };
          } catch (error) {
            console.error(`Error processing test ${index + 1}:`, error);
            toast.error(`Failed to process test ${index + 1}`);
            return null;
          }
        })
      );

      setTestsData(processedTests.map((t) => t?.data ?? null));
      setHasE12Error(processedTests.map((t) => t?.hasE12Error ?? false));
      setHasHighRH(processedTests.map((t) => t?.hasHighRH ?? false));
    };

    processAllTests();
  }, [tests]);

  const resistanceLines = useMemo<FormattedChartData[]>(() => {
    return testsData.flatMap((testData) => {
      if (!testData) {
        return [];
      }

      return Array.from({ length: 6 }, (_, sensorIndex) => ({
        id: `Sensor ${sensorIndex + 1}`,
        data: testData.resistance.slice(1).map((reading, index) => ({
          x: index,
          y: Number(reading.values[sensorIndex]),
        })),
      }));
    });
  }, [testsData]);

  const displayedResistanceLines = useMemo(
    () =>
      transformLinesForResistanceView(
        resistanceLines,
        resistanceView,
        isResistanceSmoothed
      ),
    [resistanceLines, resistanceView, isResistanceSmoothed]
  );

  const viewOptions: Array<{
    value: ResistanceViewMode;
    label: string;
  }> = useMemo(
    () => [
      { value: "raw", label: "Raw" },
      { value: "firstDerivative", label: "1st Derivative" },
      { value: "secondDerivative", label: "2nd Derivative" },
    ],
    []
  );

  const formatResistanceTick = (
    value: number,
    view: ResistanceViewMode,
    isSmoothed: boolean
  ) => {
    if (!Number.isFinite(value)) {
      return "";
    }
    if (view === "raw" && !isSmoothed) {
      return value.toExponential(0);
    }
    if (view === "raw" && isSmoothed) {
      return value.toFixed(2);
    }
    return value.toExponential(2);
  };

  const formatResistanceTooltip = (
    value: number,
    view: ResistanceViewMode,
    isSmoothed: boolean
  ) => {
    if (!Number.isFinite(value)) {
      return "NaN";
    }
    if (view === "raw" && !isSmoothed) {
      return value.toExponential(4);
    }
    if (view === "raw" && isSmoothed) {
      return value.toFixed(4);
    }
    return value.toExponential(3);
  };

  const capacitanceLines = useMemo<FormattedChartData[]>(() => {
    return testsData.flatMap((testData) => {
      if (!testData) {
        return [];
      }

      return Array.from({ length: 6 }, (_, sensorIndex) => ({
        id: `Sensor ${sensorIndex + 1}`,
        data: testData.capacitance.slice(1).map((reading, index) => ({
          x: index,
          y:
            Number(reading.values[sensorIndex]) > 1e-9
              ? Number.NaN
              : Number(reading.values[sensorIndex]),
        })),
      }));
    });
  }, [testsData]);

  const displayedCapacitanceLines = useMemo(
    () =>
      transformLinesForCapacitanceView(
        capacitanceLines,
        capacitanceView,
        isCapacitanceSmoothed
      ),
    [capacitanceLines, capacitanceView, isCapacitanceSmoothed]
  );

  const trhLines = useMemo<FormattedChartData[]>(() => {
    return testsData.flatMap((testData) => {
      if (!testData) {
        return [];
      }

      return [
        {
          id: "Cassette Temperature",
          data: testData.trh.slice(1).map((d, index) => ({
            x: index,
            y: Number(d.cassetteTemp),
          })),
        },
        {
          id: "Cassette RH",
          data: testData.trh.slice(1).map((d, index) => ({
            x: index,
            y: Number(d.cassetteRH),
          })),
        },
        {
          id: "External Temperature",
          data: testData.trh.slice(1).map((d, index) => ({
            x: index,
            y: Number(d.externalTemp),
          })),
        },
        {
          id: "External RH",
          data: testData.trh.slice(1).map((d, index) => ({
            x: index,
            y: Number(d.externalRH),
          })),
        },
        {
          id: "Internal Temperature",
          data: testData.trh.slice(1).map((d, index) => ({
            x: index,
            y: Number(d.internalTemp),
          })),
        },
        {
          id: "Internal RH",
          data: testData.trh.slice(1).map((d, index) => ({
            x: index,
            y: Number(d.internalRH),
          })),
        },
      ];
    });
  }, [testsData]);

  // TRO stage bands (heater/pump on-periods). Only unambiguous for a single
  // test - overlaid tests may have different actuator timings.
  const stageBands = useMemo(
    () => (testsData.length === 1 ? testsData[0]?.actuators ?? [] : []),
    [testsData]
  );

  const accelerometerData = useMemo(
    () => processSingleTestAccelerometerData(testsData),
    [testsData]
  );
  const defaultOrientationSample = useMemo(
    () => createDefaultOrientationSample(),
    []
  );
  const hoveredOrientationSample =
    hoveredAccelerometerIndex !== null
      ? accelerometerData.orientationSamples[hoveredAccelerometerIndex] ??
        defaultOrientationSample
      : defaultOrientationSample;

  useEffect(() => {
    setHoveredAccelerometerIndex(null);
  }, [accelerometerData.orientationSamples.length]);

  if (testsData.length === 0) {
    return <p className="text-sm text-muted-foreground">Processing data...</p>;
  }

  const formatSignedValue = (value: number) => value.toFixed(3);
  const formatSignedDegrees = (value: number) =>
    `${value > 0 ? "+" : ""}${value.toFixed(1)}°`;

  const renderWarnings = () => (
    <>
      {hasE12Error.some(Boolean) && (
        <p className="text-sm font-semibold text-destructive">
          Warning: Some resistance values exceed 2e12
        </p>
      )}
      {hasHighRH.some(Boolean) && (
        <p className="text-sm font-semibold text-destructive">
          Warning: Test begins with high humidity
        </p>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      {renderWarnings()}

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Resistance</h2>
        <div className="flex items-center gap-4">
          <ButtonGroup>
            {viewOptions.map((option) => (
              <Button
                key={option.value}
                variant={
                  resistanceView === option.value ? "default" : "outline"
                }
                onClick={() => setResistanceView(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="resistance-smooth"
              checked={isResistanceSmoothed}
              onCheckedChange={(checked) =>
                setIsResistanceSmoothed(checked as boolean)
              }
            />
            <Label htmlFor="resistance-smooth">Smooth</Label>
          </div>
        </div>
        <LazyChartSection minHeight={360}>
          <LineChartSection
            title={null}
            lines={displayedResistanceLines}
            stageBands={stageBands}
            yScale={
              resistanceView === "raw" && !isResistanceSmoothed
                ? "log"
                : undefined
            }
            skipNonPositive={resistanceView === "raw" && !isResistanceSmoothed}
            yTickFormatter={(value) =>
              typeof value === "number"
                ? formatResistanceTick(
                    value,
                    resistanceView,
                    isResistanceSmoothed
                  )
                : ""
            }
            tooltipFormatter={(value) =>
              formatResistanceTooltip(
                value,
                resistanceView,
                isResistanceSmoothed
              )
            }
          />
        </LazyChartSection>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Capacitance</h2>
        <div className="flex items-center gap-4">
          <ButtonGroup>
            {viewOptions.map((option) => (
              <Button
                key={option.value}
                variant={
                  capacitanceView === option.value ? "default" : "outline"
                }
                onClick={() => setCapacitanceView(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="capacitance-smooth"
              checked={isCapacitanceSmoothed}
              onCheckedChange={(checked) =>
                setIsCapacitanceSmoothed(checked as boolean)
              }
            />
            <Label htmlFor="capacitance-smooth">Smooth</Label>
          </div>
        </div>
        <LazyChartSection minHeight={360}>
          <LineChartSection
            title={null}
            lines={displayedCapacitanceLines}
            stageBands={stageBands}
            yTickFormatter={(value) =>
              typeof value === "number" ? value.toExponential(1) : ""
            }
            tooltipFormatter={(value) => value.toExponential(4)}
          />
        </LazyChartSection>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          Temperature &amp; Humidity
        </h2>
        <LazyChartSection minHeight={360}>
          <LineChartSection
            title={null}
            lines={trhLines}
            stageBands={stageBands}
            yTickFormatter={(value) =>
              typeof value === "number" ? value.toFixed(1) : ""
            }
            tooltipFormatter={(value, name) => {
              const suffix = name.includes("Temp") ? "°C" : "%";
              return `${value.toFixed(1)} ${suffix}`;
            }}
          />
        </LazyChartSection>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Accelerometer</h2>
        <p className="text-sm text-muted-foreground">
          Normalized accelerometer readings across X, Y, and Z. Hover the chart
          to inspect orientation and tilt.
        </p>
        {accelerometerData.lines.length ? (
          <LazyChartSection minHeight={360}>
            <LineChartSection
              title={
                <div className="flex w-full items-start justify-between gap-4">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <h3 className="text-base font-semibold text-foreground">
                        Normalized XYZ
                      </h3>
                      <span className="text-sm font-medium text-foreground">
                        {hoveredOrientationSample.orientationLabel}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {hoveredAccelerometerIndex !== null
                          ? `Sample ${hoveredAccelerometerIndex}`
                          : "Default upright preview"}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {hoveredOrientationSample.orientationDetail} X{" "}
                      {formatSignedDegrees(
                        hoveredOrientationSample.xTiltDegrees
                      )}{" "}
                      · Y{" "}
                      {formatSignedDegrees(
                        hoveredOrientationSample.yTiltDegrees
                      )}{" "}
                      · Z{" "}
                      {formatSignedDegrees(
                        hoveredOrientationSample.zTiltDegrees
                      )}
                    </p>
                  </div>
                  <OrientationCube
                    rotation={hoveredOrientationSample.rotation}
                    size={68}
                    className="shrink-0"
                  />
                </div>
              }
              lines={accelerometerData.lines}
              yTickFormatter={(value) =>
                typeof value === "number" ? value.toFixed(3) : ""
              }
              tooltipFormatter={(value) => formatSignedValue(value)}
              onHoverIndexChange={setHoveredAccelerometerIndex}
            />
          </LazyChartSection>
        ) : (
          <p className="text-sm text-muted-foreground">
            No accelerometer readings are available for this test.
          </p>
        )}
      </section>
    </div>
  );
};

export default SingleSensorDataCharts;
